import React, { Component, useRef, useEffect } from 'react';
import Sortable from 'sortablejs';
import Modal from '../modal/modal';
import Button from '../button/button';
import ItemEditorModal from '../../billboard/item_editor_modal/item_editor_modal';
import logger from '../logger';

function useClickOutside(ref, callback) {
    useEffect(() => {
      const handleClickOutside = (event) => {
        // Check if the clicked element is outside the referenced element
        if (ref.current && !ref.current.contains(event.target)) {
          callback();
        }
      };

      // Add event listener to the document
      document.addEventListener('mousedown', handleClickOutside);

      // Clean up the event listener when the component unmounts
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref, callback]); // Re-run effect if ref or callback changes
}

const ContextMenu = ({ x, y, onClose, actions }) => {
    const myRef = useRef(null);
    // Define the callback function to close the element
    const closeElement = () => {
        onClose();
    };

    // Use the custom hook
    useClickOutside(myRef, closeElement);

    return (
      <div
        ref={myRef}
        style={{
          position: 'absolute',
          top: y,
          left: x,
          zIndex: 1000, // Ensure it's on top
        }}
        className="border border-gray-200 rounded-sm bg-white shadow-lg p-2 min-w-48"
        onClick={onClose} // Close on click inside for simplicity
      >
        {actions.map((item, index) => (
            <div key={index} onClick={item.function} className="p-2 hover:bg-gray-400 cursor-pointer rounded-sm">
                <p>{item.label}</p>
            </div>
        ))}
      </div>
    );
};

class MediaGridView extends Component {

    state = {
        menu: null,
    }

    updateItem(index, item) {
        logger.log("updateItem: " + index + " " + item);
        this.props.items.find(element => element.id == item).playlist_item_index = index;
        logger.log(this.props.items);
    }

    componentDidMount() {
        this.updateItem = this.updateItem.bind(this);
        this.onContextMenu = this.onContextMenu.bind(this);
        this.clickEvent = this.clickEvent.bind(this);
        
        // Initialize Sortable if playlist_id is provided (playlist mode)
        if (this.props.playlist_id && this.props.enableSortable !== false) {
            const playlist_id = this.props.playlist_id;
            const update_function = this.updateItem;
            const playlist_items = document.getElementById('playlist_items');
            
            if (playlist_items && typeof $ !== 'undefined' && typeof Sortable !== 'undefined') {
                new Sortable.create(playlist_items, { 
                    onSort: function (/**Event*/evt) {
                        // iterate through items, and reassign their indexes
                        var item_identifiers = [];
                        $('.playlist_item').each(function(index, element) {
                            var ident = $(element).data("identifier");
                            update_function(index, ident);
                            item_identifiers.push(ident);
                        });

                        logger.log("item_identifiers: " + item_identifiers);
                        $.ajax({ 
                            url: "/billboard/playlists/" + playlist_id + "/reorder_items.json",
                            type: "POST",
                            contentType: 'application/json; charset=utf-8',
                            dataType: 'json',
                            data: JSON.stringify({ sorted_indexes: item_identifiers }),
                            success: function(data) {
                                logger.log("success reordering playlist items: " + data);
                            },
                            error: function(xhr, status, error) {
                                logger.log("error reordering playlist items: " + error);
                            }
                        });
                    },
                });
            }
        }
    }

    // Media-specific context menu actions
    editStartDate(result_id) {
        logger.log("editStartDate: " + result_id);
    }

    editEndDate(result_id) {
        logger.log("editEndDate: " + result_id);
    }

    // Playlist-specific context menu actions
    editMediaInLibrary(result_id) {
        logger.log("editMediaInLibrary: " + result_id);
    }

    // Get the item data - handles both direct items and nested billboard_media_item structure
    getItemData(item) {
        if (item.billboard_media_item) {
            return item.billboard_media_item;
        }
        return item;
    }

    // Get context menu actions based on mode
    getContextMenuActions(result_id) {
        const item = this.props.items.find(element => element.id == result_id);
        const itemData = this.getItemData(item);
        
        // Use custom actions if provided
        if (this.props.contextMenuActions) {
            return this.props.contextMenuActions.map(action => ({
                ...action,
                function: () => action.function(result_id, item, itemData)
            }));
        }
        
        // Default actions based on whether it's a playlist item or media item
        if (item.billboard_media_item) {
            // Playlist mode
            return [
                { label: "Delete", function: () => this.removeItem(result_id) },
                { label: "Edit media in library", function: () => this.editMediaInLibrary(result_id) },
            ];
        } else {
            // Media mode
            return [
                { label: "Delete", function: () => this.removeItem(result_id) },
                { label: "Edit start date", function: () => this.editStartDate(result_id) },
                { label: "Edit end date", function: () => this.editEndDate(result_id) },
            ];
        }
    }

    onContextMenu(event, result_id) {
        event.preventDefault();
        logger.log("onContextMenu");
        const actions = this.getContextMenuActions(result_id);
        this.setState({
            menu: {
                x: event.clientX,
                y: event.clientY,
                actions: actions
            }
        });
    }

    onSave(item, callback) {
        logger.log("onSave: " + item);
        this.props.onSave(item, callback);
    }
    
    onDelete(item, callback) {
        logger.log("onDelete: " + item);
        this.props.onDelete(item, callback);
    }

    clickEvent(item) {
        if (this.state && this.state.menu != null) {
            this.setState({ menu: null });
        }
        if (this.props.onSelect) {
            this.props.onSelect(item);
            return;
        }
        if (item) {
            Modal.insertModalElement(
                <ItemEditorModal item={item} onSave={(item, callback) => this.onSave(item, callback)} onDelete={(item, callback) => this.onDelete(item, callback)} />
            );
        }
    }

    clickOutside() {
        if (this.state && this.state.menu != null) {
            this.setState({ menu: null });
        }
    }

    renderItem(result, index) {
        const itemData = this.getItemData(result);
        const isActive = itemData.active !== false;
        const url = itemData.url;
        const hasAspectRatio = !result.billboard_media_item; // Only apply aspect ratio for direct media items
        
        return (
            <div 
                key={index} 
                onClick={() => this.clickEvent(result)} 
                onContextMenu={(event) => this.onContextMenu(event, result.id)} 
                data-identifier={result.id} 
                style={hasAspectRatio ? { aspectRatio: "16/9" } : {}}
                className={"playlist_item cursor-pointer rounded-lg shadow-md hover:shadow-lg border border-gray-200 overflow-hidden" + (!isActive ? " opacity-50" : "")}
            >
                <img src={url} alt="result" className="w-full h-full rounded-lg" draggable={false} />
            </div>
        );
    }

    render() {
        if (this.props.items == null) {
            return <div>Loading...</div>;
        }
        
        // Sort items by playlist_item_index if they have it (playlist mode)
        let items = this.props.items;
        if (items.length > 0 && items[0].playlist_item_index !== undefined) {
            items = [...items].sort((a, b) => a.playlist_item_index - b.playlist_item_index);
        }
        
        logger.log(items);
        return (
            <div id="playlist_items" onClick={this.clickOutside()} className={"grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-4 " + (this.props.className || "")}>
                {items.map((result, index) => (
                    this.renderItem(result, index)
                ))}
                {this.state.menu && <ContextMenu x={this.state.menu.x} y={this.state.menu.y} onClose={() => this.setState({ menu: null })} actions={this.state.menu.actions} />}
            </div>
            
        );
    }
}

export default MediaGridView;
